import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Alert } from '../../../../assets/icons/alert-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { AlertsInterface, RequestAlertsInterface } from '../../../../interface/alerts-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  disableAlertsFiltersApplied,
  fetchAlerts,
  fetchAlertsFilters,
  setSortData,
  updateAlertsFilters,
} from '../../../../redux/slices/AlertsSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../utils/api/station-management';

import AlertsChart from './AlertsChart';

/**
 * Alerts component
 * @returns {any} The rendered component
 */
function Alerts() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    alerts,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.alertsStation);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [pointInfo, setPointInfo] = useState({
    firmwareVersion: '',
  });

  /**
   * Handle fetching alerts point info
   * @param {string} chargingPointSn The charging point serial number
   */
  const handleFetchAlertsPointInfo = async (chargingPointSn: string) => {
    if (!selectedChargingPoint) return;
    try {
      const response = await stationManagementApi.fetchAlertsPointInfo({
        chargingStation: selectedChargingPoint?.id,
        chargingPointSn,
      });
      setPointInfo(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  const additionalInfo = useMemo(
    () => [
      {
        id: 1,
        label: t('originated'),
        value: '-',
      },
      {
        id: 2,
        label: t('status'),
        value: '-',
      },
      {
        id: 3,
        label: t('resolvedBy'),
        value: '-',
      },
      {
        id: 4,
        label: t('productType'),
        value: '-',
      },
      {
        id: 5,
        label: t('firmwareVer'),
        value: pointInfo.firmwareVersion,
      },
      {
        id: 6,
        label: t('cc'),
        value: '-',
      },
      {
        id: 7,
        label: t('ulListed'),
        value: '-',
      },
    ],
    [t, pointInfo.firmwareVersion],
  );

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'date' as keyof AlertsInterface,
        sortAllow: true,
        /**
         * Custom data for date
         * @param {AlertsInterface} date The alert data
         * @returns {any} The rendered date
         */
        customData: (date: AlertsInterface) => (
          <div
            role="button"
            tabIndex={0}
            className="alerts-point-info"
            onClick={() => handleFetchAlertsPointInfo(date.chargingPointSn)}
            onKeyDown={(even) =>
              even.key === 'Enter' && handleFetchAlertsPointInfo(date.chargingPointSn)
            }>
            {ConvertUTCtoDate(date.createdAt)}
          </div>
        ),
      },
      {
        label: t('time'),
        accessorKey: 'time' as keyof AlertsInterface,
        sortAllow: true,
        /**
         * Custom data for time
         * @param {AlertsInterface} time The alert data
         * @returns {any} The rendered time
         */
        customData: (time: AlertsInterface) => (
          <p>{FormatUtcString(ConvertUTCtoHHMM(time.createdAt))}</p>
        ),
      },
      {
        label: t('alerts'),
        accessorKey: 'title' as keyof AlertsInterface,
        /**
         * Custom data for alerts
         * @param {AlertsInterface} alert The alert data
         * @returns {any} The rendered alert
         */
        customData: (alert: AlertsInterface) => (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>
                <Alert />
              </span>
              <p className="table-row-each-data">{alert.title}</p>
            </div>
          </div>
        ),
        sortAllow: false,
      },
    ],
    [t],
  );

  /**
   * Handle fetching data
   * @param {RequestAlertsInterface} data The request data
   */
  const fetchData = (data: RequestAlertsInterface) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchAlerts({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };

  /**
   * Handle pagination and limit change
   * @param {any} value The pagination/limit data
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('alerts'), accessorKey: 'title' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Handle exporting data
   * @param {string} type The export type (csv or pdf)
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    stationManagementApi
      .fetchAlertsExport({
        ...queryParameters,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AlertsInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'date':
                return ConvertUTCtoDate(row.createdAt) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row.createdAt) || 'N/A';
              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'Station Activity.csv');
        } else {
          exportPDF(headers, rows, 'Station Activity.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle data refresh
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Handle sort change
   * @param {object} sortData The sort data
   * @param {'asc' | 'desc'} sortData.sortBy The sort order
   * @param {string} sortData.sortField The sort field
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableAlertsFiltersApplied());
    }
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('AlertsFiltersList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchAlertsFilters({ chargingStation: currentId }));
    }

    if (alerts.length === 0 || lastId !== currentId) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(disableAlertsFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('AlertsFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters]);

  useEffect(() => {
    if (alerts.length > 0) {
      const firstRow = alerts[0];
      if (firstRow?.chargingPointSn) {
        handleFetchAlertsPointInfo(firstRow.chargingPointSn);
      }
    }
  }, [alerts]);

  return (
    <div className="alert-container-wrap" dir={i18n.dir()}>
      <div className="alert-left-content">
        <CustomTable
          version="v2"
          header={column}
          data={alerts}
          serialNo
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          pageLimitChange={pageNateLimitChange}
          ReferenceCurrentPages={handleRefresh}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          filtersToDispatch={updateAlertsFilters}
          filterListToDispatch={fetchAlertsFilters}
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
      <div className="alert-right-content">
        <div className="alert-chart">
          <AlertsChart />
        </div>
        <div className="alert-details">
          <div className="alerts-details-header">
            <p className="alerts-details-title">{t('additionalInfo')}</p>
          </div>
          <div className="alerts-details-wrap">
            {additionalInfo.map((info) => (
              <div className="alerts-details-content" key={info.id}>
                <p>{info.label}</p>
                <p>{info.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Alerts;
